import type { GenericObjectType } from '@rjsf/utils';
import { get, isEqual, set } from 'es-toolkit/compat';
import type { TrafficCampaignFlavor } from 'generated/graphql';
import { useCallback, useMemo } from 'react';
import { useForm } from 'react-final-form';
import type { ArrayAction } from 'types/JourneysDeclarativeUI';
import { removeFromArrayInPlace, reorderArrayInPlace } from './arrayUtils';
import type { DMCCFieldSharingConfig, DMCCOverrideMap } from './variable-sharing';
import { evaluateEffectiveSharingMode, getSharingConfig } from './variable-sharing';

function validateArrayAction(action: ArrayAction, sourceArray?: unknown[]): boolean {
  // biome-ignore lint/nursery/noUnnecessaryConditions: legacy code
  if (action.actionType === 'add') return sourceArray ? Array.isArray(sourceArray) && sourceArray.length > 0 : true;
  if (action.actionType === 'remove') return action.sourceIndex !== undefined;
  if (action.actionType === 'reorder') return action.sourceIndex !== undefined && action.targetIndex !== undefined;
  return false;
}

function applyContentOperation<T>(action: ArrayAction, targetArray: T[], sourceArray?: T[]): T[] | undefined {
  if (action.actionType === 'add') {
    return [
      ...targetArray,
      sourceArray!.at(-1)!,
    ];
  }

  if (action.actionType === 'remove') {
    const newArray = [
      ...targetArray,
    ];
    removeFromArrayInPlace(newArray, action.sourceIndex!);
    return newArray;
  }

  // reorder
  const newArray = [
    ...targetArray,
  ];
  reorderArrayInPlace(newArray, action.sourceIndex!, action.targetIndex!);
  return newArray;
}

function applyConfigOperation(action: ArrayAction, targetArray: DMCCOverrideMap[]): DMCCOverrideMap[] {
  const newArray = [
    ...targetArray,
  ];
  if (action.actionType === 'add') newArray.push({} as DMCCOverrideMap);
  else if (action.actionType === 'remove') removeFromArrayInPlace(newArray, action.sourceIndex!);
  else reorderArrayInPlace(newArray, action.sourceIndex!, action.targetIndex!);
  return newArray;
}

function processContentArraySync<GenericObjectType>(
  action: ArrayAction,
  targetArray: GenericObjectType[],
  sourceArray: GenericObjectType[],
): GenericObjectType[] | undefined {
  if (!validateArrayAction(action, sourceArray)) return;

  // Check for sync mismatch with built-in failsafe behavior
  let lengthAdjustment = 0;
  if (action.actionType === 'add') lengthAdjustment = 1;
  else if (action.actionType === 'remove') lengthAdjustment = -1;

  if (sourceArray.length !== targetArray.length + lengthAdjustment)
    return [
      ...sourceArray,
    ];

  return applyContentOperation(action, targetArray, sourceArray);
}

function processConfigArraySync(action: ArrayAction, targetArray: DMCCOverrideMap[]): DMCCOverrideMap[] | undefined {
  // Use unified validation (config operations don't require sourceArray for 'add')
  if (!validateArrayAction(action)) return targetArray;

  const newArray = applyConfigOperation(action, targetArray);

  // Apply cleanup logic with sparse storage management
  if (newArray.some((item) => item && typeof item === 'object' && Object.keys(item).length > 0)) return newArray;
}

/**
 * Custom hook for synchronizing array operations across DMCC campaign flavors with BRD evaluation integration.
 *
 * Provides comprehensive array synchronization utilities for managing both content arrays and DMCC
 * configuration arrays across multiple campaign flavors. Uses BRD (Business Requirements Document)
 * evaluation to determine when configuration synchronization should occur based on effective sharing
 * modes. Supports add, remove, and reorder operations with built-in validation and failsafe mechanisms
 * for handling sync mismatches.
 *
 * The hook integrates with the DMCC field sharing system to maintain alignment between content data
 * and configuration overrides, ensuring that array structure changes are consistently applied across
 * all active flavors while respecting user-defined sharing preferences.
 *
 * @param currentFlavorIndex - Index of the current flavor being edited (used to exclude from sync operations)
 * @param fieldSharingConfig - DMCC field sharing configuration instance for managing override maps and BRD evaluation
 * @param flavors - Array of all campaign flavors with content data and active status
 * @returns Object containing synchronization utilities:
 *   - syncArrayActionAcrossFlavors: Syncs content array operations across other active flavors
 *   - syncBothArrays: Combined sync for both content and configuration arrays with BRD evaluation
 *   - syncConfigurationArrays: Syncs DMCC configuration arrays using BRD evaluation for sharing mode determination
 */
export function useArraySyncHandler({
  currentFlavorIndex,
  fieldSharingConfig,
  flavors,
}: {
  currentFlavorIndex: number;
  fieldSharingConfig: DMCCFieldSharingConfig;
  flavors: TrafficCampaignFlavor[];
}) {
  const formApi = useForm();

  // Extract only the necessary data to reduce dependency thrashing
  const targetFlavors = useMemo(
    () =>
      flavors
        .map((flavor, index) => ({
          ...flavor,
          targetFlavorIndex: index, // Keep reference for array operations
        }))
        .filter(({ active, targetFlavorIndex }) => active && targetFlavorIndex !== currentFlavorIndex),
    [
      currentFlavorIndex,
      flavors,
    ],
  );

  /**
   * Synchronizes an array action across all other flavors
   */
  const syncArrayActionAcrossFlavors = useCallback(
    (newFormData: unknown, action: ArrayAction) => {
      if (targetFlavors.length === 0) return;

      const { path } = action;
      const newFormDataArray = get(newFormData, path);
      if (!Array.isArray(newFormDataArray)) return;

      formApi.batch(() =>
        targetFlavors.forEach(({ content, targetFlavorIndex }) => {
          const targetArray = get(content, path);
          if (!Array.isArray(targetArray)) return;
          const newArray = processContentArraySync(action, targetArray, newFormDataArray);
          if (newArray) formApi.change(`flavors[${targetFlavorIndex}].content.${path}`, newArray);
        }),
      );
    },
    [
      targetFlavors,
      formApi,
    ],
  );

  /**
   * Synchronizes configuration arrays with BRD evaluation integration.
   */
  const syncConfigurationArrays = useCallback(
    (action: ArrayAction, uiSchema?: GenericObjectType) => {
      const currentOverrideMap = fieldSharingConfig.dmccOverrideMap;
      if (!currentOverrideMap) return;

      // Use BRD evaluation to determine if sync should occur
      if (
        // biome-ignore lint/nursery/noUnnecessaryConditions: legacy code
        uiSchema &&
        !evaluateEffectiveSharingMode({
          dmccOverrideMap: fieldSharingConfig.dmccOverrideMap,
          // it is ok to rely on splitting here as array paths don't have weird structures
          fieldPathArray: action.path.split('.'),
          sharingConfig: getSharingConfig(action.path.split('.'), uiSchema),
        })
      )
        return; // Skip sync if BRD evaluation indicates no sharing should occur

      // Get the current configuration array for this path
      let currentConfigArray = get(currentOverrideMap, action.path);
      if (!Array.isArray(currentConfigArray)) currentConfigArray = [];

      // Apply the same array operation to the configuration
      const newConfigArray = processConfigArraySync(action, currentConfigArray);

      // Handle cleanup for empty or undefined arrays
      if (!newConfigArray || (Array.isArray(newConfigArray) && newConfigArray.length === 0)) {
        // Remove the field override entirely for empty arrays
        return fieldSharingConfig.removeFieldOverride(action.path as never);
      }

      // Update the configuration if it changed
      if (!isEqual(newConfigArray, currentConfigArray)) {
        fieldSharingConfig.setOverrideMap(set(currentOverrideMap, action.path, newConfigArray));
      }
    },
    [
      fieldSharingConfig,
    ],
  );

  /**
   * Combined synchronization for both content and configuration arrays.
   */
  const syncBothArrays = useCallback(
    (newFormData: unknown, action: ArrayAction, uiSchema?: GenericObjectType) => {
      // Sync content arrays first
      syncArrayActionAcrossFlavors(newFormData, action);

      // Then sync configuration arrays with BRD evaluation
      syncConfigurationArrays(action, uiSchema);
    },
    [
      syncArrayActionAcrossFlavors,
      syncConfigurationArrays,
    ],
  );

  return {
    // Base functionality (backward compatible)
    syncArrayActionAcrossFlavors,
    syncBothArrays,

    // Enhanced functionality for configuration support
    syncConfigurationArrays,
  };
}
